import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const SLUG = 'tatouaz-athina-touristes';
const URL = `https://210tattoo.gr/blog/${SLUG}`;

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Μπορώ να κάνω τατουάζ στην Αθήνα ως τουρίστας;", acceptedAnswer: { "@type": "Answer", text: "Ναι, απόλυτα. Πολλά σοβαρά στούντιο, όπως το 210 Tattoo Athens, δέχονται τακτικά διεθνείς επισκέπτες και προσφέρουν επικοινωνία στα αγγλικά και ολοκληρωμένη υποστήριξη." } },
    { "@type": "Question", name: "Πότε στο ταξίδι μου πρέπει να κάνω το τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Εξαρτάται από το πρόγραμμά σου. Αν σχεδιάζεις θάλασσα ή έντονο ήλιο, κάνε το τατουάζ είτε αρκετά νωρίς πριν είτε μετά από αυτές τις δραστηριότητες, ώστε να προλάβει η επιφανειακή επούλωση." } },
    { "@type": "Question", name: "Πρέπει να προγραμματίσω το ραντεβού πριν φτάσω στην Αθήνα;", acceptedAnswer: { "@type": "Answer", text: "Είναι πολύ συνετό, ειδικά τους καλοκαιρινούς μήνες με αυξημένη τουριστική κίνηση. Η επικοινωνία εκ των προτέρων εξασφαλίζει διαθεσιμότητα και σου εξοικονομεί χρόνο." } },
    { "@type": "Question", name: "Μπορώ να κάνω τατουάζ με ελληνικό κείμενο;", acceptedAnswer: { "@type": "Answer", text: "Ναι, αλλά βεβαιώσου ότι συνεργάζεσαι με καλλιτέχνες που έχουν πραγματική γνώση της γλώσσας, ώστε να αποφευχθούν λάθη στη γραφή ή στο νόημα." } },
    { "@type": "Question", name: "Επηρεάζει η πτήση της επιστροφής το φρέσκο τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Όχι άμεσα. Το τατουάζ δεν επηρεάζεται από την πίεση της καμπίνας. Χρειάζεται όμως τη συνήθη φροντίδα, οπότε φρόντισε να έχεις μαζί σου τα απαραίτητα προϊόντα." } },
  ],
};

export default function TatouazAthinaTouristes() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Τατουάζ στην Αθήνα για Τουρίστες: Τι Πρέπει να Γνωρίζεις",
      "description": "Οδηγός για επισκέπτες: χρόνος επούλωσης, ήλιος και θάλασσα, επιλογή στούντιο, ελληνικό κείμενο και πρακτικά ζητήματα για τατουάζ στην Αθήνα ως τουρίστας.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-07-07",
      "dateModified": "2026-07-07",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": URL,
      "inLanguage": "el"
    };
    inject('schema-tourists-faq', faqSchema);
    inject('schema-tourists-article', articleSchema);
    return () => {
      document.getElementById('schema-tourists-faq')?.remove();
      document.getElementById('schema-tourists-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τατουάζ στην Αθήνα για Τουρίστες | 210 Tattoo Athens</title>
        <meta name="description" content="Ταξιδεύεις στην Αθήνα και σκέφτεσαι τατουάζ; Χρόνος επούλωσης, ήλιος και θάλασσα, επιλογή στούντιο, ελληνικό κείμενο και όλα όσα πρέπει να ξέρεις ως επισκέπτης." />
        <link rel="canonical" href={URL} />
        <link rel="alternate" hrefLang="el" href={URL} />
        <link rel="alternate" hrefLang="x-default" href={URL} />
        <meta property="og:title" content="Τατουάζ στην Αθήνα για Τουρίστες | 210 Tattoo Athens" />
        <meta property="og:description" content="Χρόνος επούλωσης, ήλιος και θάλασσα, επιλογή στούντιο και πρακτικά ζητήματα για τατουάζ στην Αθήνα ως τουρίστας." />
        <meta property="og:url" content={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage(SLUG)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τατουάζ στην Αθήνα για Τουρίστες | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Ό,τι πρέπει να ξέρεις πριν κάνεις τατουάζ στην Αθήνα ως επισκέπτης." />
        <meta name="twitter:image" content={ogImage(SLUG)} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Τατουάζ για Τουρίστες</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Τατουάζ στην Αθήνα για τουρίστες</span>
              <br />
              <span className="text-white">τι πρέπει να γνωρίζεις</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">7 Ιουλίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Ένα τατουάζ ως θύμηση του ταξιδιού σου στην Αθήνα έχει τις δικές του ιδιαιτερότητες. Δες τι χρειάζεσαι, τι να προσέξεις και πώς να έχεις εξαιρετική εμπειρία ακόμα κι αν είσαι εδώ για λίγες μέρες.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Why Athens */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί η Αθήνα είναι <span className="text-pink-500">εξαιρετική επιλογή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Η Αθήνα έχει μια ζωντανή, ώριμη σκηνή τατουάζ, με καλλιτέχνες που έχουν διεθνή εμπειρία και εκπαίδευση. Πολλοί έχουν ταξιδέψει, έχουν δουλέψει σε άλλες χώρες και έχουν συμμετάσχει σε διεθνή conventions. Η ποιότητα που θα βρεις εδώ δεν υστερεί σε τίποτα σε σχέση με μεγάλες ευρωπαϊκές πρωτεύουσες.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Επιπλέον, η πόλη προσφέρει μια αίσθηση ιστορίας και πολιτισμού που μπορεί να εμπνεύσει το σχέδιό σου με τρόπο μοναδικό. Πολλοί επισκέπτες βρίσκουν έμπνευση σε αρχαία σύμβολα, στη μυθολογία ή στη φύση της χώρας. Οι τιμές είναι συχνά πιο προσιτές σε σχέση με άλλες μεγάλες ευρωπαϊκές πόλεις, χωρίς να θυσιάζεται η ποιότητα.
            </p>
          </div>
        </section>

        {/* Timing considerations */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Τι να σκεφτείς <span className="text-pink-500">πριν αποφασίσεις</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ως επισκέπτης, ο χρόνος και το πρόγραμμά σου παίζουν μεγάλο ρόλο στο πότε πρέπει να κάνεις το τατουάζ.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: 'Ο χρόνος επούλωσης', desc: 'Ένα τατουάζ χρειάζεται δύο έως τρεις εβδομάδες για την επιφανειακή του επούλωση. Αν το κάνεις νωρίς, θα το φροντίζεις όσο εξερευνάς, αν το κάνεις στο τέλος, θα συνεχίσεις τη φροντίδα μετά την επιστροφή σου.' },
                { title: 'Ήλιος και θάλασσα', desc: 'Ένα φρέσκο τατουάζ δεν πρέπει να εκτεθεί σε θάλασσα, πισίνα ή έντονο ήλιο μέχρι να επουλωθεί. Αν σχεδιάζεις νησιά ή παραλίες, κάνε το τατουάζ πριν ή μετά αυτές τις δραστηριότητες.' },
                { title: 'Η πτήση της επιστροφής', desc: 'Το τατουάζ δεν επηρεάζεται από την πίεση της καμπίνας. Χρειάζεται όμως τη συνήθη φροντίδα, οπότε έχε μαζί σου τα απαραίτητα προϊόντα καθαρισμού και ενυδάτωσης.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Αν το ταξίδι σου περιλαμβάνει παραλία, ο οδηγός μας για το <Link to="/blog/sea-after-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πότε μπορείς να πας θάλασσα μετά από τατουάζ</Link> εξηγεί ακριβώς πόσο πρέπει να περιμένεις.
            </p>
          </div>
        </section>

        {/* Choosing a studio */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να επιλέξεις σωστό στούντιο <span className="text-pink-500">ως τουρίστας</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Ως τουρίστας έχεις λιγότερο χρόνο για έρευνα και λιγότερη δυνατότητα να επιστρέψεις αν κάτι πάει στραβά. Αυτό κάνει τη σωστή επιλογή ακόμα πιο κρίσιμη.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Ψάξε online πριν φτάσεις', desc: 'Δες portfolios στο Instagram, διάβασε κριτικές, δες φωτογραφίες επουλωμένων τατουάζ. Έχεις χρόνο να συγκρίνεις χωρίς την πίεση του περιορισμένου χρόνου.' },
                { title: 'Επικοινώνησε εκ των προτέρων', desc: 'Στείλε μήνυμα πριν έρθεις. Περίγραψε την ιδέα σου, ρώτησε για διαθεσιμότητα και εκτίμηση κόστους. Έτσι εξοικονομείς πολύτιμο χρόνο όταν φτάσεις.' },
                { title: 'Βεβαιώσου για την υγιεινή', desc: 'Ζήτα να δεις στείρες βελόνες μιας χρήσης, καθαρό εξοπλισμό και γάντια. Ένα σοβαρό στούντιο δεν θα έχει πρόβλημα να σου δείξει τις πρακτικές του.' },
                { title: 'Σκέψου τη γλωσσική επικοινωνία', desc: 'Βεβαιώσου ότι το στούντιο επικοινωνεί άνετα στα αγγλικά ή στη γλώσσα σου, ώστε να μη χαθεί τίποτα στη μετάφραση της ιδέας σου.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <div>
                    <p className="text-white font-semibold mb-1">{item.title}</p>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Greek-inspired ideas */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ιδέες εμπνευσμένες <span className="text-pink-500">από την Αθήνα και την Ελλάδα</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Αν θες κάτι που θα θυμίζει το ταξίδι σου, υπάρχουν πολλές όμορφες κατευθύνσεις.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Αρχαία σύμβολα και μυθολογία', desc: 'Θεότητες, σύμβολα δύναμης και φιλοσοφικές έννοιες με διαχρονική αισθητική αξία που δεν εξαρτάται από τη μόδα.' },
                { title: 'Ελληνικά γράμματα και φράσεις', desc: 'Λέξεις ή φράσεις στα ελληνικά, αρχαία ή νέα. Βεβαιώσου ότι ο καλλιτέχνης κατανοεί καλά τη γλώσσα, ώστε να αποφευχθούν λάθη.' },
                { title: 'Στοιχεία της φύσης', desc: 'Η ελιά, η θάλασσα, ο ήλιος και τα κύματα συνδέονται βαθιά με την ελληνική εμπειρία και μεταφράζονται σε όμορφα, οργανικά σχέδια.' },
                { title: 'Αρχιτεκτονικά στοιχεία', desc: 'Στήλες, γεωμετρικά μοτίβα ή μια στυλιζαρισμένη Ακρόπολη δίνουν ένα τατουάζ με σαφή σύνδεση με την πόλη.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-base leading-relaxed mt-6">
              Αν δεν έχεις καταλήξει σε σχέδιο, ρίξε μια ματιά στο άρθρο μας για το <Link to="/blog/pos-briskeis-idea-tatouaz-athina" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πώς βρίσκεις ιδέα για τατουάζ στην Αθήνα</Link>. Και αν θες ελληνικό κείμενο, βεβαιώσου ότι συνεργάζεσαι με στούντιο που έχει πραγματική γνώση της γλώσσας, ώστε η γραμματική και το νόημα να είναι σωστά.
            </p>
          </div>
        </section>

        {/* Practical & central location */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πρακτικά ζητήματα <span className="text-pink-500">και κεντρική τοποθεσία</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Ρώτα εκ των προτέρων ποιοι τρόποι πληρωμής γίνονται δεκτοί, ειδικά αν προγραμματίζεις μεγάλη δουλειά. Τα σοβαρά στούντιο έχουν συχνά πλήρη προγράμματα τους καλοκαιρινούς μήνες, οπότε προγραμμάτισε το ραντεβού σου νωρίς. Αν συνεχίζεις το ταξίδι σου, φρόντισε να έχεις μαζί σου τα απαραίτητα προϊόντα μεταφροντίδας, ειδικά αν πρόκειται να ταξιδέψεις σε ζέστη ή σε παραλία.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              Το κέντρο της Αθήνας, όπου βρίσκεται το 210 Tattoo Athens, είναι εξαιρετικό σημείο για να συνδυάσεις το ραντεβού σου με τουριστικές δραστηριότητες. Είσαι κοντά στην Ακρόπολη, στο Μοναστηράκι και στο Σύνταγμα, οπότε μπορείς εύκολα να κάνεις το ραντεβού το πρωί και να συνεχίσεις την περιήγηση το απόγευμα. Πριν το ραντεβού, δες και τι <Link to="/blog/what-to-eat-before-tattoo-athens" className="text-pink-400 hover:text-pink-300 underline underline-offset-4">πρέπει να φας πριν από το τατουάζ</Link> για πιο ομαλή εμπειρία.
            </p>
          </div>
        </section>

        {/* 210 approach */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Γιατί το 210 Tattoo Athens <span className="text-pink-500">για τουρίστες</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Δεχόμαστε τακτικά επισκέπτες από όλο τον κόσμο και έχουμε προσαρμόσει την εμπειρία μας ώστε να είναι εξαιρετική για διεθνείς πελάτες.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Επικοινωνία στα αγγλικά', desc: 'Η ομάδα επικοινωνεί άνετα στα αγγλικά, κάτι που κάνει τη συνεργασία με διεθνείς πελάτες ομαλή και ξεκάθαρη.' },
                { title: 'Προγραμματισμός πριν την άφιξη', desc: 'Απαντάμε σε μηνύματα από επισκέπτες τακτικά και μπορούμε να κλείσουμε το ραντεβού σου πριν καν φτάσεις.' },
                { title: 'Γραπτές οδηγίες μεταφροντίδας', desc: 'Αναλυτικές οδηγίες που μπορείς να συμβουλευτείς και μετά την επιστροφή σου στη χώρα σου.' },
                { title: 'Σωστό ελληνικό κείμενο', desc: 'Ως Έλληνες, μπορούμε να επαληθεύσουμε ότι οτιδήποτε ελληνικό στο τατουάζ σου είναι γραμματικά σωστό και σημασιολογικά ακριβές.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200">
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Σχεδιάζεις ταξίδι στην Αθήνα και θες <span className="text-pink-500">ένα τατουάζ ως θύμηση;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Στείλε μήνυμα πριν φτάσεις, περίγραψε την ιδέα σου και τις ημερομηνίες του ταξιδιού σου, και η ομάδα θα σε καθοδηγήσει σε όλη τη διαδικασία, από τον σχεδιασμό μέχρι τη σωστή φροντίδα κατά την επιστροφή σου.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε Μήνυμα
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/summer-tattoo-care-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Τατουάζ το Καλοκαίρι</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Ήλιος, θάλασσα και σωστή φροντίδα για καλοκαιρινό tattoo.</p>
              </Link>
              <Link to="/blog/best-time-for-tattoo-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Ποια Εποχή είναι Καλύτερη για Τατουάζ;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πώς να επιλέξεις το σωστό timing με βάση το πρόγραμμά σου.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-950 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/lettering-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Lettering Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
